import { GYM_RULES } from "../types/gym.types";

// All UI date math is in the browser's local time. ISO strings are y-m-d.

export function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Monday of the week containing d (Mon–Sun weeks), time zeroed. */
export function startOfWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dayFromMonday = (x.getDay() + 6) % 7; // Sun=0 -> 6, Mon=1 -> 0
  x.setDate(x.getDate() - dayFromMonday);
  return x;
}

export function startOfToday(): Date {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate());
}

export function isSameDay(a: Date, b: Date): boolean {
  return toISODate(a) === toISODate(b);
}

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** The 7 Date objects for a week starting at weekStart (Monday). */
export function weekDays(weekStart: Date): { date: Date; name: string; dayOfMonth: number }[] {
  return DAY_NAMES.map((name, i) => {
    const date = addDays(weekStart, i);
    return { date, name, dayOfMonth: date.getDate() };
  });
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** e.g. "18 – 24 Aug 2026", collapsing month/year when shared. */
export function formatWeekRange(weekStart: Date): string {
  const end = addDays(weekStart, 6);
  const sameMonth = weekStart.getMonth() === end.getMonth();
  const sameYear = weekStart.getFullYear() === end.getFullYear();
  const left = sameMonth
    ? `${weekStart.getDate()}`
    : `${weekStart.getDate()} ${MONTHS[weekStart.getMonth()]}${sameYear ? "" : ` ${weekStart.getFullYear()}`}`;
  const right = `${end.getDate()} ${MONTHS[end.getMonth()]} ${end.getFullYear()}`;
  return `${left} – ${right}`;
}

const FULL_DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** e.g. "Thursday, 20 Aug 2026" from an ISO date string. */
export function formatFullDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return `${FULL_DAY_NAMES[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** A booking's slotId is `${date}_${startTime}` — split it back out for display. */
export function parseBookingSlotId(slotId: string): { date: string; startTime: string } {
  const [date, startTime] = slotId.split("_");
  return { date, startTime };
}

/**
 * Client-side hint only — mirrors the server's isBookingCancellable so the UI
 * can disable the button and explain why, but the API re-checks authoritatively.
 */
export function isBookingCancellable(date: string, startTime: string): boolean {
  const slotStart = new Date(`${date}T${startTime}:00`);
  const cutoffMs = GYM_RULES.cancellationCutoffHours * 60 * 60 * 1000;
  return slotStart.getTime() - Date.now() >= cutoffMs;
}

/** Is this the daily slot reserved for female employees? */
export function isLadiesOnlySlot(startTime: string): boolean {
  return GYM_RULES.ladiesOnlyStartTime !== null && startTime === GYM_RULES.ladiesOnlyStartTime;
}

/** Canonical slot start times, mirroring the API generator. */
export function slotStartTimes(): string[] {
  const [oh, om] = GYM_RULES.opensAt.split(":").map(Number);
  const [ch, cm] = GYM_RULES.closesAt.split(":").map(Number);
  const open = oh * 60 + om;
  const close = ch * 60 + cm;
  const times: string[] = [];
  for (let t = open; t + GYM_RULES.slotMinutes <= close; t += GYM_RULES.slotMinutes) {
    times.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
  }
  return times;
}
